import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Paper,
    Popover,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
} from '@mui/material'
import { DateTime } from 'luxon'
import {
    displayPrice,
    downloadFile,
    formatDateForDatabase,
    isSelectingText,
    paginate,
    toInitials,
} from '@utils'
import { logout } from '@utils/sessions'
import { LoadingButton } from '@mui/lab'
import { useDebouncedCallback } from 'use-debounce'
import { useEffect, useState } from 'react'
import DashboardBreadcrumbs from '@pages/Dashboard/components/DashboardBreadcrumbs'
import DashboardErrorView from '@pages/Dashboard/components/DashboardErrorView'
import DashboardLayout from '@pages/Dashboard/components/DashboardLayout'
import DashboardLoadingDataView from '@pages/Dashboard/components/DashboardLoadingDataView'
import DatePicker from '@components/DatePicker'
import DownloadButton from '../../../components/DownloadButton'
import SelectedRowDetailsDialog from '@components/SelectedRowDetailsDialog'
import TableCellSubText from '@components/TableCellSubText'
import TableHeadCell from '@components/TableHeadCell'
import useSession from '@hooks/useSession'
import PeriodDatePicker from '@components/PeriodDatePicker'
import API from '@enums/api'
import { STAFF_REPORTS_URL } from '@enums/api'

export type CompletedOrderRow = {
    id: string
    organization_name: string
    user_email: string
    user_last_name: string
    user_first_name: string
    turnaround_time: number
    created_date: string
    date_finished: string
    actual_turnaround_time_hours: string
    late: string
    title: string
    duration_mins: number
    rate: number | null
    cost: number | null
    currency: string | null
    user_id: string
    org_id: string
}

const DEFAULT_FROM_DATE = DateTime.now().startOf('month')
const DEFAULT_TO_DATE = DateTime.now()
const REPORT_API_ENDPOINT = `${STAFF_REPORTS_URL}/completed-orders`

const CompletedOrdersReportPage = () => {
    const [loading, setLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)
    const [rows, setRows] = useState<CompletedOrderRow[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [downloadError, setDownloadError] = useState<string | null>(null)
    const [selectedRow, setSelectedRow] = useState<CompletedOrderRow | null>(
        null
    )
    const session = useSession()!

    const [fromDate, setFromDate] = useState<DateTime | undefined | null>(
        DEFAULT_FROM_DATE
    )
    const [toDate, setToDate] = useState<DateTime | undefined | null>(
        DEFAULT_TO_DATE
    )
    const [queriedFromDate, setQueriedFromDate] = useState<
        DateTime | undefined | null
    >(DEFAULT_FROM_DATE)
    const [queriedToDate, setQueriedToDate] = useState<
        DateTime | undefined | null
    >(DEFAULT_TO_DATE)

    // search state
    const [search, setSearch] = useState('')
    const handleSearchChange = useDebouncedCallback((event) => {
        setSearch(event.target.value.trim())
        setPage(0)
    }, 250)

    // pagination state
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [page, setPage] = useState(0)
    const handlePageChange = (_: unknown, newPage: number) => setPage(newPage)
    const handleRowsPerPageChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const [anchorElement, setAnchorElement] =
        useState<HTMLButtonElement | null>(null)

    const popoverOpen = Boolean(anchorElement)

    useEffect(() => {
        async function load() {
            setLoading(true)
            setPage(0)

            const from = formatDateForDatabase(queriedFromDate, 'start')
            const to = formatDateForDatabase(queriedToDate, 'end')
            const query = `?from=${from}&to=${to}`

            const res = await API.reports.completedOrders(from, to)
            const response = await fetch(REPORT_API_ENDPOINT + query, {
                headers: {
                    Authorization: `Bearer ${session.accessToken}`,
                },
            })
            setLoading(false)
            if (!res.ok) {
                if (response.status === 401) {
                    logout()
                    return
                }
                console.error(res)
                setError('Failed to generate report')
            } else {
                setRows(res.value)
            }
        }
        load()
    }, [queriedFromDate, queriedToDate])

    // download report as csv
    async function handleDownload() {
        setDownloading(true)
        const from = formatDateForDatabase(queriedFromDate, 'start')
        const to = formatDateForDatabase(queriedToDate, 'end')
        const query = `?from=${from}&to=${to}`
        try {
            await downloadFile({
                acceptType: 'text/csv',
                fileURL: REPORT_API_ENDPOINT + query,
                saveAsFileName: `completed-orders_${from}-${to}.csv`,
            })
            setDownloading(false)
        } catch (error) {
            setDownloading(false)
            setDownloadError('Failed to download report')
        }
    }

    // date query change
    async function handleDateQueryChange() {
        setQueriedFromDate(fromDate)
        setQueriedToDate(toDate)
        // and then fetch the new results
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (rows === null) {
        return <DashboardLoadingDataView loading={loading} />
    }

    let filteredRows = rows

    if (search) {
        const lSearch = search.toLowerCase()

        filteredRows = filteredRows.filter((row) => {
            if (row.user_email.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.user_first_name.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.user_last_name.toLowerCase().includes(lSearch)) {
                return true
            }

            if (
                row.organization_name &&
                row.organization_name.toLowerCase().includes(lSearch)
            ) {
                return true
            }

            if (row.title.toLowerCase().includes(lSearch)) {
                return true
            }

            // match presentation of last name, first name in table
            const lastNameFirstName =
                `${row.user_last_name}, ${row.user_first_name}`.toLowerCase()

            if (lastNameFirstName.toLowerCase().includes(lSearch)) {
                return true
            }

            // match natural written  name
            const firstNameLastName = `${row.user_first_name} ${row.user_last_name}`
            if (firstNameLastName.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.id.toLowerCase().includes(lSearch)) {
                return true
            }

            if (row.user_id.toLowerCase().includes(lSearch)) {
                return true
            }

            return false
        })
    }

    const rowCount = filteredRows.length
    filteredRows = paginate(filteredRows, page, rowsPerPage)

    const dateQueryHasChanged =
        fromDate?.toMillis() != queriedFromDate?.toMillis() ||
        toDate?.toMillis() != queriedToDate?.toMillis()

    return (
        <DashboardLayout loading={loading} hideBreadcrumbs>
            <Dialog
                onClose={() => setDownloadError(null)}
                open={!!downloadError}
            >
                <DialogTitle>{downloadError}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Sometimes this can be resolved by logging out and back
                        in again. If that does not work, please reach out to one
                        of the development team and we will investigate the
                        issue.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDownloadError(null)} autoFocus>
                        Dismiss
                    </Button>
                </DialogActions>
            </Dialog>
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                }}
            >
                <DashboardBreadcrumbs />
                <Box style={{ display: 'flex', marginTop: 8 }}>
                    <Stack direction="row">
                        <Button
                            style={{ marginRight: 16 }}
                            onClick={(e) => setAnchorElement(e.currentTarget)}
                        >
                            Quick Set
                        </Button>
                        <Popover
                            open={popoverOpen}
                            anchorEl={anchorElement}
                            onClose={() => setAnchorElement(null)}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'center',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'center',
                            }}
                        >
                            <Paper sx={{ padding: 1 }}>
                                <PeriodDatePicker
                                    fromDate={fromDate}
                                    setFromDate={setFromDate}
                                    toDate={toDate}
                                    setToDate={setToDate}
                                />
                            </Paper>
                        </Popover>
                        <div style={{ marginRight: 16 }}>
                            <DatePicker
                                label="From"
                                onChange={setFromDate}
                                value={fromDate}
                                maxDate={toDate}
                            />
                        </div>
                        <div style={{ marginRight: 16 }}>
                            <DatePicker
                                label="To"
                                onChange={setToDate}
                                value={toDate}
                                minDate={fromDate}
                            />
                        </div>
                        <LoadingButton
                            onClick={handleDateQueryChange}
                            loading={loading}
                            disabled={loading || !dateQueryHasChanged}
                        >
                            Apply
                        </LoadingButton>
                    </Stack>
                </Box>
            </div>
            <SelectedRowDetailsDialog
                onClose={() => setSelectedRow(null)}
                selectedRow={selectedRow}
            />
            <div
                style={{
                    marginBottom: 16,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                }}
            >
                <div>
                    <TextField
                        label="Search"
                        variant="standard"
                        onChange={handleSearchChange}
                    />
                </div>
                <div
                    style={{
                        textAlign: 'center',
                    }}
                >
                    <p
                        style={{
                            color: 'var(--grey-700)',
                            fontSize: '0.875rem',
                        }}
                    >
                        <strong>{rowCount} results</strong>
                    </p>
                    {queriedFromDate != null && queriedToDate != null && (
                        <p
                            style={{
                                color: 'var(--grey-400)',
                                fontSize: '0.75rem',
                            }}
                        >
                            Currently showing completed orders from{' '}
                            <strong>
                                {queriedFromDate.toLocaleString(
                                    DateTime.DATE_SHORT
                                )}
                            </strong>{' '}
                            to{' '}
                            <strong>
                                {queriedToDate.toLocaleString(
                                    DateTime.DATE_SHORT
                                )}
                            </strong>
                        </p>
                    )}
                </div>
                <div>
                    <DownloadButton
                        downloading={downloading}
                        onClick={handleDownload}
                    >
                        Download CSV
                    </DownloadButton>
                </div>
            </div>
            <TableContainer
                component={Paper}
                sx={{
                    maxWidth: 'calc(100vw - 240px - 32px)',
                    width: '100%',
                }}
            >
                <Table
                    size="small"
                    sx={{
                        tableLayout: 'fixed',
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableHeadCell>
                                Organization
                                <br />
                                <TableCellSubText>Initials</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell
                                sx={{
                                    width: 220,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                Name
                                <br />
                                <TableCellSubText>Email</TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>
                                Service
                                <br />
                                <TableCellSubText>
                                    Turnaround Time
                                </TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>
                                Date Completed
                                <br />
                                <TableCellSubText>
                                    Date Order Created
                                </TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>
                                Actual Turnaround
                                <br />
                                <TableCellSubText>
                                    Late / On-time
                                </TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell>
                                Media Source
                                <br />
                                <TableCellSubText>
                                    Media Duration
                                </TableCellSubText>
                            </TableHeadCell>
                            <TableHeadCell align="right">
                                Cost
                                <br />
                                <TableCellSubText>Rate/min</TableCellSubText>
                            </TableHeadCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredRows.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    No results found
                                    <br />
                                    <TableCellSubText>
                                        Try changing your search or filters
                                    </TableCellSubText>
                                </TableCell>
                            </TableRow>
                        ) : (
                            filteredRows.map((row, index) => {
                                return (
                                    <TableRow
                                        key={index}
                                        onClick={() => {
                                            // set the selected row info
                                            // which will also open up a
                                            // modal with more information
                                            if (!isSelectingText()) {
                                                setSelectedRow(row)
                                            }
                                        }}
                                        hover
                                    >
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.organization_name}
                                            <br />
                                            <TableCellSubText>
                                                {toInitials(
                                                    row.organization_name || ''
                                                )}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.user_last_name},{' '}
                                            {row.user_first_name}
                                            <br />
                                            <TableCellSubText>
                                                {row.user_email}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell>
                                            {row.turnaround_time.toString()} hrs
                                        </TableCell>
                                        <TableCell>
                                            {DateTime.fromISO(
                                                row.date_finished
                                            ).toLocaleString(
                                                DateTime.DATE_SHORT
                                            )}
                                            <br />
                                            <TableCellSubText>
                                                {DateTime.fromISO(
                                                    row.created_date
                                                ).toLocaleString(
                                                    DateTime.DATE_SHORT
                                                )}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell>
                                            {row.actual_turnaround_time_hours}
                                            <small>hrs</small>
                                            <br />
                                            <TableCellSubText>
                                                {row.late === 'TRUE'
                                                    ? 'Late'
                                                    : 'On-time'}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                textOverflow: 'ellipsis',
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {row.title}
                                            <br />
                                            <TableCellSubText>
                                                {row.duration_mins}
                                                {row.duration_mins === 1
                                                    ? 'min'
                                                    : 'mins'}
                                            </TableCellSubText>
                                        </TableCell>
                                        <TableCell align="right">
                                            <code>
                                                <strong>
                                                    {row.currency != null &&
                                                    row.cost !== null
                                                        ? displayPrice(
                                                              row.currency,
                                                              row.cost / 100
                                                          )
                                                        : 'Unknown'}
                                                </strong>
                                            </code>
                                            <br />
                                            <TableCellSubText>
                                                <code>
                                                    {row.currency != null &&
                                                    row.rate !== null
                                                        ? displayPrice(
                                                              row.currency,
                                                              row.rate / 100
                                                          )
                                                        : 'Unknown'}
                                                </code>
                                            </TableCellSubText>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={rowCount}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                page={page}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 20, 50, { value: -1, label: 'All' }]}
            />
        </DashboardLayout>
    )
}

export default CompletedOrdersReportPage
